import { useState, useEffect } from "react";
import { NavLink, useLocation, Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Building2, FolderKanban, Lightbulb, Columns3, GanttChartSquare,
  Route as RouteIcon, CalendarDays, FileText, ClipboardList, Settings, Shield, Sparkles,
  Wand2, ListChecks, BarChart3, CreditCard, ChevronRight, ChevronDown, ChevronUp,
  TrendingUp, FileBarChart2, FileDown, Target, ListTodo, ShieldAlert, LayoutDashboard, Cable
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useApp } from "@/context/AppContext";
import { useTenant } from "@/context/TenantContext";
import { useS360Consult } from "@/context/S360ConsultContext";
import { useV360Features } from "@/hooks/useV360Features";
import { V360_STAGES } from "@/data/v360Data";
import { useMgcAtivosCount } from "@/lib/mgcStore";
import { isN1 } from "@/lib/papeis";
import { Sheet, SheetContent } from "@/components/ui/sheet";

// Constrói a navegação Sprints360 na ordem canônica solicitada:
//   INTELIGÊNCIA · GOVERNANÇA · OPERAÇÃO
// (Ações e Decisões + Análise por IA são renderizadas fora deste helper,
//  no cabeçalho fixo do S360Nav.)
// Quando o plano Consultoria está ativo, "Empresas" aparece em GOVERNANÇA
// entre o cabeçalho da seção e "Projetos".
const buildNavS360 = (t, { hasConsultoria } = {}) => [
  { group: t("nav.governanca"), items: [
    ...(hasConsultoria ? [{ to: "/app/s360-empresas", label: "Empresas", icon: Building2, testid: "nav-s360-empresas" }] : []),
    { to: "/app/dashboard",      label: "Dashboard",         icon: LayoutDashboard,  testid: "nav-dashboard" },
    { to: "/app/projetos",       label: t("nav.projetos"),   icon: FolderKanban,     testid: "nav-projetos" },
    { to: "/app/gantt",          label: t("nav.cronograma"), icon: GanttChartSquare, testid: "nav-gantt" },
  ]},
  { group: t("nav.operacao"), items: [
    { to: "/app/sprint-board",   label: "Quadro de Atividades", icon: Columns3,      testid: "nav-sprint-board" },
    { to: "/app/calendario",     label: t("nav.calendario"),    icon: CalendarDays,  testid: "nav-calendario" },
    // Phase 10.53 — "Jornada" removida; "Acompanhamento" ocupa seu lugar aqui.
    { to: "/app/painel-semanal", label: "Acompanhamento",       icon: Sparkles,      testid: "nav-painel-semanal" },
    { to: "/app/reunioes",       label: t("nav.reunioes_atas"), icon: ClipboardList, testid: "nav-reunioes" },
    { to: "/app/documentos",     label: t("nav.documentos"),    icon: FileText,      testid: "nav-documentos" },
  ]},
];

// Bloco ADMINISTRAÇÃO (antigo WORKSPACE). "Colaboradores" foi removido — a
// tela era do V360 legado e não mais mantida.
const buildNavWorkspace = (t) => [
  { to: "/app/subscricoes",   label: "Planos",                    icon: CreditCard, testid: "nav-subscricoes" },
  { to: "/app/usuarios",      label: t("nav.usuarios_permissoes"), icon: Shield,     testid: "nav-usuarios" },
  { to: "/app/configuracoes", label: t("common.settings"),         icon: Settings,   testid: "nav-configuracoes" },
  { to: "/app/configuracoes/conexao", label: "Central de Conexão", icon: Cable,      testid: "nav-central-conexao" },
];

export default function Sidebar({ mobileOpen, onMobileOpenChange }) {
  const { pathname } = useLocation();
  const { setModuloAtual, moduloAtual, hasV360, hasSprints } = useApp();
  // Prioridade: rotas V360 explícitas > rotas Sprints360 explícitas > moduloAtual
  // (útil em telas neutras como /app/welcome — Phase 7.40b bugfix).
  const inV360Route     = pathname.startsWith("/app/v360");
  const inSprintsRoute  = [
    "/app/dashboard", "/app/projetos", "/app/recomendacoes",
    "/app/sprint-board", "/app/gantt", "/app/calendario",
    "/app/documentos", "/app/reunioes", "/app/painel-semanal",
    "/app/sprints360", "/app/atividades", "/app/analise-ia-s360",
    "/app/s360-empresas", "/app/s360/",
  ].some(p => pathname.startsWith(p));
  const isV360 = inV360Route || (!inSprintsRoute && moduloAtual === "valora360");
  const accent = isV360 ? "violet" : "blue";

  // Fecha automaticamente o off-canvas ao mudar de rota
  useEffect(() => { onMobileOpenChange?.(false); }, [pathname, onMobileOpenChange]);

  return (
    <>
      {/* Desktop: sidebar sticky */}
      <aside data-testid="app-sidebar"
             className="hidden lg:flex w-64 shrink-0 border-r border-slate-200 bg-white flex-col h-screen sticky top-0">
        <SidebarBody isV360={isV360} accent={accent} setModuloAtual={setModuloAtual} hasV360={hasV360} hasSprints={hasSprints} />
      </aside>

      {/* Mobile: off-canvas Sheet */}
      <Sheet open={!!mobileOpen} onOpenChange={onMobileOpenChange}>
        <SheetContent side="left" className="p-0 w-72 bg-white border-r border-slate-200" data-testid="mobile-sidebar">
          <div className="flex flex-col h-full">
            <SidebarBody isV360={isV360} accent={accent} setModuloAtual={setModuloAtual} hasV360={hasV360} hasSprints={hasSprints} mobile />
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

function SidebarBody({ isV360, accent, setModuloAtual, hasV360 = true, hasSprints = true, mobile }) {
  const { planoCustomizado } = useApp();
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const { empresaAtual } = useApp();
  const empresaParam = params.get("empresa") || (empresaAtual?.__live ? empresaAtual.id : "");
  const v360Target = `/app/v360/empresa-dashboard${empresaParam ? `?empresa=${empresaParam}` : ""}`;

  return (
    <>
      {/* 1. Logo VALORA360 */}
      <div className="px-5 py-4 border-b border-slate-200">
        <Link to="/app/dashboard" className="flex items-center justify-center" data-testid="sidebar-logo">
          <img src="/luoggo-logo.png" alt="Luoggo" className="h-[25px] w-auto" />
        </Link>
      </div>

      {/* 2. Seletor de Apps — Phase 12.29: quando o cliente NÃO tem V360,
             o botão V360 fica escondido (grid vira 1 coluna). Quando não
             tem S360 mas tem V360, mantemos o S360 desabilitado como
             convite comercial (Phase 11.9). */}
      <div className="px-3 py-3 border-b border-slate-200 bg-slate-50/40">
        <div className="text-[10px] uppercase tracking-[0.12em] text-slate-400 font-semibold px-1 mb-1.5">{t("nav.aplicativo_ativo")}</div>
        <div className={cn(
          "gap-1 bg-white border border-slate-200 rounded-md p-1 grid",
          hasV360 ? "grid-cols-2" : "grid-cols-1"
        )} data-testid="sidebar-app-switcher">
          {hasV360 && (
            <Link to={v360Target} onClick={() => setModuloAtual("valora360")}
                  data-testid="app-switch-v360"
                  className={cn("flex items-center gap-1.5 px-2 py-1.5 rounded text-xs font-bold transition-all justify-center flex-1",
                    isV360 ? "bg-violet-700 text-white shadow-sm" : "text-slate-600 hover:bg-slate-50")}>
              Gestão
            </Link>
          )}
          {hasSprints ? (
            <Link to="/app/dashboard" onClick={() => setModuloAtual("sprints360")}
                  data-testid="app-switch-s360"
                  className={cn("flex items-center gap-1.5 px-2 py-1.5 rounded text-xs font-bold transition-all justify-center flex-1",
                    !isV360 ? "bg-blue-700 text-white shadow-sm" : "text-slate-600 hover:bg-slate-50")}>
              Projetos
            </Link>
          ) : (
            <button type="button" disabled aria-disabled="true"
                    data-testid="app-switch-s360-disabled"
                    title="Seu plano não inclui Luoggo Projetos — faça upgrade"
                    className="flex items-center gap-1.5 px-2 py-1.5 rounded text-xs font-bold justify-center text-slate-400 bg-slate-50 cursor-not-allowed opacity-70">
              Projetos
            </button>
          )}
        </div>
        {hasV360 && !hasSprints && !planoCustomizado && (
          <p className="text-[9px] text-slate-400 mt-1.5 px-1 leading-tight">
            Seu plano inclui apenas V360 LITE. Faça upgrade para desbloquear o Luoggo Projetos.
          </p>
        )}
      </div>

      {/* 3. Menu do app ativo */}
      <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-0.5">
        {isV360 ? <V360Nav accent={accent} /> : <S360Nav accent={accent} />}
      </nav>

      {/* 4. Bloco WORKSPACE retrátil */}
      <WorkspaceBlock accent={accent} />

      <div className="px-4 py-2.5 border-t border-slate-200">
        <div className="text-[11px] text-slate-500">Luoggo GI @ 2026 - v.2.1{mobile ? " · Mobile" : ""}</div>
      </div>
    </>
  );
}

function WorkspaceBlock({ accent }) {
  const { t } = useTranslation();
  const NAV_WORKSPACE = buildNavWorkspace(t);
  const [open, setOpen] = useState(false); // começa fechado
  const { pathname } = useLocation();
  const isWorkspaceRoute = [
    "/app/subscricoes", "/app/usuarios", "/app/configuracoes",
  ].some(p => pathname.startsWith(p));
  // Auto-close ao mudar de rota (clique num item)
  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <div className="border-t border-slate-200 bg-slate-50/60" data-testid="sidebar-workspace-block">
      <div
        className={cn("overflow-hidden transition-[max-height,opacity] duration-300 ease-out",
          open ? "max-h-[320px] opacity-100" : "max-h-0 opacity-0")}
        aria-hidden={!open}
      >
        <div className="px-3 pt-3 pb-1 space-y-0.5">
          {NAV_WORKSPACE.map(item => <NavItem key={item.to} {...item} accent={accent} />)}
        </div>
      </div>

      <button
        onClick={() => setOpen(v => !v)}
        data-testid="workspace-toggle"
        aria-expanded={open}
        className={cn("w-full flex items-center gap-2 px-4 py-2.5 transition-colors",
          open ? "border-t border-slate-200" : "",
          isWorkspaceRoute && !open ? "bg-blue-50/40" : "",
          "hover:bg-slate-100")}
      >
        <span className={cn("w-1 h-1 rounded-full", isWorkspaceRoute ? "bg-blue-600" : "bg-slate-400")} />
        <span className={cn("flex-1 text-left text-[10px] tracking-[0.12em] uppercase font-bold",
          isWorkspaceRoute ? "text-blue-700" : "text-slate-500")}>ADMINISTRAÇÃO</span>
        <ChevronUp className={cn("w-3.5 h-3.5 transition-transform duration-300",
          isWorkspaceRoute ? "text-blue-500" : "text-slate-400",
          open ? "rotate-180" : "rotate-0")} />
      </button>
    </div>
  );
}

function S360Nav({ accent }) {
  const { t } = useTranslation();
  const { hasConsultoria } = useS360Consult();
  const NAV_S360 = buildNavS360(t, { hasConsultoria });
  return (
    <>
      {/* === Inteligência === */}
      <div className="px-2 pt-2 pb-1.5 text-[10px] tracking-[0.12em] uppercase text-slate-400 font-semibold">
        {t("nav.inteligencia")}
      </div>
      <NavItem
        to="/app/s360/acoes-decisoes"
        label="Ações e Decisões"
        icon={ShieldAlert}
        testid="nav-s360-acoes-decisoes"
        accent="orange"
      />
      <NavItem
        to="/app/analise-ia-s360"
        label={t("nav.analise_por_ia")}
        icon={Sparkles}
        testid="nav-analise-ia-s360"
        accent="orange"
      />

      {NAV_S360.map(g => (
        <div key={g.group} className="mt-3 border-t border-slate-100">
          <div className="px-2 pt-2 pb-1.5 text-[10px] tracking-[0.12em] uppercase text-slate-400 font-semibold">{g.group}</div>
          {g.items.map(item => <NavItem key={item.to} {...item} accent={accent} />)}
        </div>
      ))}
    </>
  );
}

function V360Nav({ accent }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  const { empresaAtual, hasCustomMetrics, currentUser } = useApp();
  // Phase 9.05 — Nível bloqueia o bloco Personalização inteiro.
  // OKRS_PROPRIOS é ATIVO só em N3. Se DESABILITADO neste nível → esconde o bloco.
  const { isDesabilitado, loading: featuresLoading } = useV360Features();
  const personalizacaoBloqueada = !featuresLoading && isDesabilitado("OKRS_PROPRIOS");
  const onWizard = pathname === "/app/v360/wizard";
  const activeStage = params.get("stage");
  const activeFrente = params.get("frente");
  const [expanded, setExpanded] = useState(false);
  const mgcAtivosCount = useMgcAtivosCount();

  const empresaParam =
    params.get("empresa") || (empresaAtual?.__live ? empresaAtual.id : "");
  const empAmp = empresaParam ? `&empresa=${empresaParam}` : "";

  return (
    <div>
      {/* === Análise por IA === */}
      <div className="px-2 pt-2 pb-1.5 text-[10px] tracking-[0.12em] uppercase text-slate-400 font-semibold">{t("nav.inteligencia")}</div>
      <NavItem to="/app/acoes-decisoes" label="Ações e Decisões" icon={ShieldAlert} testid="nav-acoes-decisoes" accent="orange" badge={mgcAtivosCount > 0 ? mgcAtivosCount : null} />
      {/* Jun/2026 — E6 (Análise Integrada) é N1-only no backend. */}
      {isN1(currentUser) && (
        <NavItem to="/app/analise-ia" label={t("nav.analise_por_ia")} icon={Sparkles} testid="nav-analise-ia" accent="fuchsia" />
      )}

      {/* === Visão da Empresa === */}
      <div className="mt-3 px-2 pt-2 pb-1.5 text-[10px] tracking-[0.12em] uppercase text-slate-400 font-semibold border-t border-slate-100">{t("nav.visao_da_empresa")}</div>
      <NavItem to={`/app/v360/empresa-dashboard${empresaParam ? `?empresa=${empresaParam}` : ""}`}  label={t("nav.painel_v360")} icon={BarChart3}     testid="nav-v360-empresa-dashboard" accent={accent} />
      <NavItem to={`/app/v360/empresa-resumo${empresaParam ? `?empresa=${empresaParam}` : ""}`}     label={t("nav.resumo")}      icon={FileBarChart2} testid="nav-v360-empresa-resumo"    accent={accent} />
      <NavItem to={`/app/v360/empresa-evolucao${empresaParam ? `?empresa=${empresaParam}` : ""}`}   label={t("nav.evolucao")}    icon={TrendingUp}    testid="nav-v360-empresa-evolucao"  accent={accent} />
      <NavItem to={`/app/v360/relatorios${empresaParam ? `?empresa=${empresaParam}` : ""}`}         label={t("nav.relatorios")}  icon={FileDown}      testid="nav-v360-relatorios"        accent={accent} />

      {/* === Diagnóstico === */}
      <div className="mt-3 px-2 pt-2 pb-1.5 text-[10px] tracking-[0.12em] uppercase text-slate-400 font-semibold border-t border-slate-100">{t("nav.diagnostico")}</div>
      <NavItem to={`/app/v360/wizard${empresaParam ? `?empresa=${empresaParam}` : ""}`} label={t("nav.wizard_com_val")} icon={Wand2} testid="nav-v360-wizard" accent={accent} />

      {/* Menu Estruturado — header expansível */}
      <button
        onClick={() => setExpanded(v => !v)}
        data-testid="nav-v360-menu"
        className={cn(
          "w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-sm transition-colors",
          expanded ? "text-slate-900 bg-slate-50" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
        )}
      >
        <ListChecks className="w-[18px] h-[18px] text-slate-500" />
        <span className="flex-1 text-left truncate">{t("nav.menu_estruturado")}</span>
        {expanded ? <ChevronDown className="w-3.5 h-3.5 text-slate-400" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400" />}
      </button>

      {expanded && (
        <div className="ml-3 pl-3 mt-1 border-l border-slate-200 space-y-0.5" data-testid="v360-menu-tree">
          <div className="px-2 pt-1 pb-1 text-[9px] tracking-[0.12em] uppercase text-slate-400 font-bold">{t("wizard.etapa")}s</div>

          {V360_STAGES.map(s => {
            const isActive = onWizard && activeStage === s.id && !activeFrente;
            const stageLabel = t(`wizard.stages.${s.id}`, { defaultValue: s.label });
            return (
              <div key={s.id}>
                <NavLink
                  to={`/app/v360/wizard?stage=${s.id}${empAmp}`}
                  data-testid={`menu-stage-${s.id}`}
                  className={cn(
                    "flex items-center gap-2 px-2 py-1.5 rounded text-[12px] transition-colors min-w-0",
                    isActive
                      ? "bg-violet-50 text-violet-800 font-semibold"
                      : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  )}
                >
                  <span className={cn("w-5 h-5 grid place-items-center rounded text-[10px] font-bold shrink-0",
                    isActive ? "bg-violet-700 text-white" : "bg-slate-100 text-slate-500"
                  )}>{s.num}</span>
                  <span className="truncate uppercase tracking-wide text-[10.5px] font-semibold">{stageLabel}</span>
                </NavLink>
              </div>
            );
          })}
        </div>
      )}

      {/* === Personalização — feature-gated (visível apenas quando OKRS_PROPRIOS ATIVO) === */}
      {hasCustomMetrics && !personalizacaoBloqueada && (
        <>
          <div className="mt-3 px-2 pt-2 pb-1.5 text-[10px] tracking-[0.12em] uppercase text-slate-400 font-semibold border-t border-slate-100">{t("nav.personalizacao")}</div>
          <NavItem to="/app/personalizacao/okrs"    label="OKRs"    icon={Target}    testid="nav-personalizacao-okrs"    accent={accent} />
          <NavItem to="/app/personalizacao/kpis"    label="KPIs"    icon={BarChart3} testid="nav-personalizacao-kpis"    accent={accent} />
          <NavItem to="/app/personalizacao/volumes" label="Volumes" icon={TrendingUp} testid="nav-personalizacao-volumes" accent={accent} />
        </>
      )}
    </div>
  );
}

function NavItem({ to, label, icon: Icon, testid, end, accent, badge }) {
  const activeCls =
    accent === "violet" ? "bg-violet-50 text-violet-800 font-medium" :
    accent === "orange" ? "bg-orange-50 text-orange-800 font-medium" :
                          "bg-blue-50 text-blue-800 font-medium";
  const activeIconCls =
    accent === "violet" ? "text-violet-700" :
    accent === "orange" ? "text-orange-700" :
                          "text-blue-700";
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={testid}
      className={({ isActive }) =>
        cn(
          "flex items-center gap-2.5 px-2.5 py-2 rounded-md text-sm transition-colors",
          isActive ? activeCls : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
        )
      }
    >
      {({ isActive }) => (
        <>
          <Icon className={cn("w-[18px] h-[18px]",
            isActive ? activeIconCls : "text-slate-500")} />
          <span className="truncate flex-1">{label}</span>
          {badge != null && badge !== "" && (
            typeof badge === "number" ? (
              <span
                data-testid={`${testid}-badge`}
                className="text-[10px] font-bold px-1.5 min-w-[20px] h-[18px] inline-flex items-center justify-center rounded-full bg-orange-500 text-white shadow-sm">
                {badge}
              </span>
            ) : (
              <span className="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 border border-orange-200">
                {badge}
              </span>
            )
          )}
        </>
      )}
    </NavLink>
  );
}
